import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Story } from "./types";
import { StoryForm } from "./StoryForm";
import { MediaViewer } from "./MediaViewer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Loader2, Pencil, Trash2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useHasCollaborators } from "@/hooks/useHasCollaborators";
import { useStoryLock } from "@/hooks/useStoryLock";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface StoryDialogProps {
  story: Story;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isViewMode: boolean;
  setIsViewMode: (isViewMode: boolean) => void;
  onUpdate: (story: Story) => void;
  onSave: () => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onMediaUpload: (photos: string[]) => void;
  onMediaRemove: (index: number) => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

export const StoryDialog = ({
  story,
  isOpen,
  setIsOpen,
  isViewMode,
  setIsViewMode,
  onUpdate,
  onSave,
  onDelete,
  onMediaUpload,
  onMediaRemove,
  isSaving = false,
  readOnly = false
}: StoryDialogProps) => {
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);

  // Check if memorial has collaborators
  const { hasCollaborators, isLoading: isLoadingCollaborators } = useHasCollaborators(story.memorial_id);

  // Story locking (only activates when collaborators exist)
  const { lockStatus, isAcquiringLock, acquireLock, releaseLock } = useStoryLock(
    story.id,
    hasCollaborators,
    () => {
      // Force back to view mode if lock is lost
      setIsViewMode(true);
    }
  );

  // Convert storage path to public URL if needed
  const getPublicUrl = (path: string): string => {
    if (!path) return "";

    // If it already looks like a full URL, return it
    if (path.startsWith('http')) return path;

    // Otherwise, get the public URL from the storage path
    try {
      const { data: { publicUrl } } = supabase.storage
        .from('memorials')
        .getPublicUrl(path);
      return publicUrl;
    } catch (error) {
      console.error('Error getting public URL:', error);
      return "";
    }
  };

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const enableScrolling = () => {
    document.body.style.removeProperty('overflow');
  };

  useEffect(() => {
    if (isOpen) {
      const timeouts = [100, 300, 500, 1000].map(delay =>
        setTimeout(enableScrolling, delay)
      );

      return () => timeouts.forEach(clearTimeout);
    }
  }, [isOpen]);

  // Scroll to top when switching to edit mode
  useEffect(() => {
    if (!isViewMode && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [isViewMode]);

  // Clear preview files when dialog closes or switches to view mode
  useEffect(() => {
    if (!isOpen || isViewMode) {
      setPreviewFiles([]);
    }
  }, [isOpen, isViewMode]);

  // Handle file preview callback
  const handleFilePreview = (files: File[]) => {
    setPreviewFiles(files);
  };

  // Handle successful save by switching back to view mode
  const handleSaveSuccess = async () => {
    await releaseLock();
    setIsViewMode(true);
  };

  // Handle edit button click
  const handleEditClick = async () => {
    if (hasCollaborators) {
      // Try to acquire lock
      const lockAcquired = await acquireLock();
      if (lockAcquired) {
        setIsViewMode(false);
      }
    } else {
      // No collaborators, skip locking
      setIsViewMode(false);
    }
  };

  // Handle dialog close
  const handleDialogClose = async (open: boolean) => {
    if (isSaving) return;

    if (!open) {
      await releaseLock();
      setIsViewMode(true);
      setPreviewFiles([]);
      enableScrolling();
    }
    setIsOpen(open);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleDialogClose}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-visible">
        <DialogHeader>
          <DialogTitle>{story.title}</DialogTitle>
        </DialogHeader>

        {/* Lock status indicator */}
        {hasCollaborators && lockStatus.isLocked && !lockStatus.isLockedByMe && (
          <Alert className="border-destructive">
            <Lock className="h-4 w-4" />
            <AlertDescription>
              This story is currently being edited by another user. View only mode.
            </AlertDescription>
          </Alert>
        )}

        {hasCollaborators && lockStatus.isLockedByMe && !isViewMode && (
          <Alert>
            <Lock className="h-4 w-4" />
            <AlertDescription>
              You are editing this story. Lock auto-refreshes every 2 minutes.
            </AlertDescription>
          </Alert>
        )}
        <ScrollArea ref={scrollAreaRef} className="h-[calc(90vh-8rem)] overflow-y-auto">
          <div className="space-y-4 pt-4 px-1">
            {isViewMode ? (
              <div className="space-y-6">
                <div className="prose max-w-none">
                  {story.story_date && (
                    <p className="text-sm text-gray-500 mb-2">
                      {new Date(story.story_date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap">{story.content}</p>
                </div>

                {story.media && story.media.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {story.media.map((mediaItem, index) => (
                      <div key={mediaItem.id || index} className="aspect-square overflow-hidden rounded-md">
                        {mediaItem.media_type === 'image' || (typeof mediaItem.media_type === 'string' && mediaItem.media_type.startsWith('image/')) ? (
                          <img
                            src={getPublicUrl(mediaItem.url)}
                            alt={mediaItem.caption || 'Story image'}
                            className="w-full h-full object-cover"
                          />
                        ) : mediaItem.media_type === 'video' || (typeof mediaItem.media_type === 'string' && mediaItem.media_type.startsWith('video/')) ? (
                          mediaItem.stream_id ? (
                            <iframe
                              src={`https://iframe.videodelivery.net/${mediaItem.stream_id}`}
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full border-0"
                            />
                          ) : (
                            <video
                              src={getPublicUrl(mediaItem.url)}
                              controls
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}

                {!readOnly && (
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={handleEditClick}
                      className="flex items-center gap-2"
                      disabled={isAcquiringLock || (hasCollaborators && lockStatus.isLocked && !lockStatus.isLockedByMe)}
                    >
                      {isAcquiringLock ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Pencil className="h-4 w-4" />
                      )}
                      {hasCollaborators && lockStatus.isLocked && !lockStatus.isLockedByMe ? 'Locked' : 'Edit'}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={onDelete}
                      className="flex items-center gap-2"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <StoryForm
                story={story}
                onUpdate={onUpdate}
                onSave={onSave}
                onMediaUpload={onMediaUpload}
                onMediaRemove={onMediaRemove}
                onFilePreview={handleFilePreview}
                previewFiles={previewFiles}
                buttonText="Update Story"
                isSaving={isSaving}
                usePreviewMode={true}
                onSaveSuccess={handleSaveSuccess}
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
